import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  UIButton,
  UIForm,
  UIInput,
  UISpin,
  LockOutlined,
  Logout,
  Person,
  UserOutlined,
} from "ui";
import { ROUTE_URLS, USER_ROLES } from "../../constants/consts";
import { useSignIn } from "../../hooks/UseSignIn.hook";
import {
  checkUserRole,
  clearLocalStorage,
  getIsTwoFactorAuthorized,
  getIsUserLoggedIn,
  getSession,
  getSessionUserCompany,
  getSessionUserRole,
  getTwoFactorEnabled,
  setSession,
  setSessionUserRole,
} from "../../api/sessionManagement";
import { clearUserLanguageCache } from "../../hooks/useUserLanguage.hook";
import "./SignIn.sass";

export const SignIn = () => {
  const { handleSignIn } = useSignIn();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isActiveSession, setIsActiveSession] = useState(false);
  const [isAutoContinuingSession, setIsAutoContinuingSession] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  useEffect(() => {
    setIsActiveSession(Boolean(getSession() && getIsUserLoggedIn()));
  }, []);

  const validateForm = (
    nextEmail: string = email,
    nextPassword: string = password
  ) => {
    const errors: { email?: string; password?: string } = {};
    const emailValue = nextEmail.trim();
    const passwordValue = nextPassword.trim();

    if (!emailValue) {
      errors.email = t("required_field");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      errors.email = t("order_delivery_info.invalid_email");
    }

    if (!passwordValue) {
      errors.password = t("required_field");
    }

    return errors;
  };

  useEffect(() => {
    if (!submitAttempted) {
      return;
    }

    setValidationErrors(validateForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password, submitAttempted, t]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isLoading) {
      return;
    }

    setSubmitAttempted(true);
    const errors = validateForm();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsLoading(true);
    try {
      await handleSignIn(email, password);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueSession = useCallback(async () => {
    const isTwoFactorAuthorized = getIsTwoFactorAuthorized();
    const isTwoFactorEnabled = getTwoFactorEnabled();
    const result = await checkUserRole();

    if (result.error) {
      return;
    }

    setSession(result.data.data.session);
    setSessionUserRole(result.data.data.role);
    const userRole = getSessionUserRole();

    if (
      isTwoFactorEnabled &&
      !isTwoFactorAuthorized &&
      result.data.data.is_need_verify
    ) {
      navigate(ROUTE_URLS.TWO_FACTOR_AUTH);
      return;
    }

    if (userRole === USER_ROLES.ACTIVE) {
      navigate(ROUTE_URLS.HOME);
      return;
    }

    navigate(ROUTE_URLS.QUESTIONARY);
  }, [navigate]);

  useEffect(() => {
    if (!isActiveSession) {
      return;
    }

    setIsAutoContinuingSession(true);
    void handleContinueSession().finally(() => {
      setIsAutoContinuingSession(false);
    });
  }, [handleContinueSession, isActiveSession]);

  const handleLogout = async () => {
    clearLocalStorage();
    clearUserLanguageCache();

    const browserLang = navigator.language || "en-GB";
    await i18n.changeLanguage(browserLang);

    setEmail("");
    setPassword("");
    setSubmitAttempted(false);
    setValidationErrors({});
    setIsActiveSession(false);
  };

  const isSubmitDisabled = isLoading;

  if (isAutoContinuingSession) {
    return (
      <div className="sign-in-session">
        <UIButton
          type="button"
          variant="text"
          className="sign-in-session__card"
          disabled
        >
          <span className="sign-in-session__card-content">
            <span className="sign-in-session__icon">
              <Person />
            </span>
            <span className="sign-in-session__text">
              <span className="sign-in-session__company">
                {getSessionUserCompany()}
              </span>
              <span className="sign-in-session__description">
                <Trans i18nKey="accountExists" />
              </span>
            </span>
          </span>
        </UIButton>
      </div>
    );
  }

  if (isActiveSession) {
    return (
      <div className="sign-in-session">
        <UIButton
          type="button"
          variant="text"
          className="sign-in-session__card"
          onClick={handleContinueSession}
        >
          <span className="sign-in-session__card-content">
            <span className="sign-in-session__icon">
              <Person />
            </span>
            <span className="sign-in-session__text">
              <span className="sign-in-session__company">
                {getSessionUserCompany()}
              </span>
              <span className="sign-in-session__description">
                <Trans i18nKey="accountExists" />
              </span>
            </span>
          </span>
        </UIButton>

        <UIButton
          type="button"
          variant="outlined"
          className="sign-in-session__logout"
          onClick={handleLogout}
        >
          <span className="sign-in-session__logout-content">
            <Logout />
            <span>
              <Trans i18nKey="logout" />
            </span>
          </span>
        </UIButton>
      </div>
    );
  }

  return (
    <UIForm className="sign-in-form" onSubmit={handleSubmit} noValidate>
      <div className="sign-in-form__field">
        <label htmlFor="sign-in-email" className="sign-in-form__label">
          <Trans i18nKey="email" />
        </label>
        <UIInput
          id="sign-in-email"
          type="email"
          value={email}
          required
          autoComplete="username"
          className={`sign-in-form__input ${
            validationErrors.email ? "sign-in-form__input--error" : ""
          }`}
          prefix={<UserOutlined className="sign-in-form__prefix" />}
          onChange={(event) => setEmail(event.target.value)}
        />
        {validationErrors.email ? (
          <p className="sign-in-form__error">{validationErrors.email}</p>
        ) : null}
      </div>

      <div className="sign-in-form__field">
        <label htmlFor="sign-in-password" className="sign-in-form__label">
          <Trans i18nKey="password" />
        </label>
        <UIInput
          id="sign-in-password"
          type="password"
          value={password}
          required
          autoComplete="current-password"
          className={`sign-in-form__input ${
            validationErrors.password ? "sign-in-form__input--error" : ""
          }`}
          prefix={<LockOutlined className="sign-in-form__prefix" />}
          onChange={(event) => setPassword(event.target.value)}
        />
        {validationErrors.password ? (
          <p className="sign-in-form__error">{validationErrors.password}</p>
        ) : null}
      </div>

      <UIButton
        type="submit"
        variant="contained"
        fullWidth
        className="sign-in-form__submit"
        disabled={isSubmitDisabled}
      >
        {isLoading ? <UISpin size="small" /> : <Trans i18nKey="login" />}
      </UIButton>

      <p className="sign-in-form__meta">
        <Trans i18nKey="noAccount" />{" "}
        <Link to={ROUTE_URLS.SIGN_UP}>
          <Trans i18nKey="signUp" />
        </Link>
      </p>

      <p className="sign-in-form__meta sign-in-form__meta--secondary">
        <Trans i18nKey="forgot_your_password" />{" "}
        <Link to={ROUTE_URLS.RECOVER_PASSWORD}>
          <Trans i18nKey="recover_password" />
        </Link>
      </p>
    </UIForm>
  );
};
